import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  return localStorage.getItem("fl_session_id") || "";
}

const AuthContext = createContext(null);

let refreshing = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const url = cfg.url || "";
    if (error.response?.status === 401 && !cfg._retried && url.startsWith(API) && !url.includes("/auth/")) {
      cfg._retried = true;
      try {
        refreshing = refreshing || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).finally(() => { refreshing = null; });
        await refreshing;
        return axios(cfg);
      } catch (e) { /* fall through */ }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Returning from Emergent Google sign-in: AuthCallback exchanges the session_id first, skip /me here.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = (next) => {
    if (next) sessionStorage.setItem("fl_google_next", next); else sessionStorage.removeItem("fl_google_next");
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const sid = getSessionId();
    const response = await axios.post(`${API}/auth/google/session${sid ? `?guest_session_id=${sid}` : ""}`, { session_id: sessionId }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Access token expired: try to renew it from the refresh cookie ("Ricordami")
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const retry = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(retry.data);
        return retry.data;
      } catch (e) {
        setUser(null);
        return null;
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const sid = getSessionId();
    const response = await axios.post(
      `${API}/auth/login${sid ? `?session_id=${sid}` : ""}`,
      { email, password, remember_me: rememberMe },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, role = "customer", privacyConsent = false) => {
    const sid = getSessionId();
    const response = await axios.post(
      `${API}/auth/register${sid ? `?session_id=${sid}` : ""}`,
      { email, password, name, role, privacy_consent: privacyConsent },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth, loginWithGoogle, exchangeGoogleSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
